import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getVertebraColor } from '../styles/colorUtils';
import ctSliceCache from '../services/ctSliceCache';
import API_URL from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

function CTSliceViewer({ viewType = 'Axial', selectedLevel, onSliceChange, showOverlay = true }) {

  const [loading, setLoading] = useState(true);
  const [volumeLoading, setVolumeLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlice, setCurrentSlice] = useState(0);
  const [totalSlices, setTotalSlices] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartSlice = useRef(0);
  const [prominentVertebra, setProminentVertebra] = useState(null);
  const [sliceData, setSliceData] = useState(null);


  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const renderRequestRef = useRef(null);
  const offscreenCanvasRef = useRef(null);

  // On initial mount or view change, load the volume info and preload all slices
  useEffect(() => {
    const loadVolumeAndInfo = async () => {
      try {
        setVolumeLoading(true);
        setVolumeLoading(true);

        // Don't clear cache - keep all views loaded

        const response = await fetch(`${API_URL}/nifti/info`, { headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(`Failed to fetch NIFTI info: ${response.statusText}`);
        }

        const data = await response.json();
        // Nifti info loaded

        // Set total slices based on view type
        const viewLower = viewType.toLowerCase();
        if (data.ct && data.ct.slices && data.ct.slices[viewLower]) {
          const count = data.ct.slices[viewLower];
          setTotalSlices(count);
          setVolumeLoading(false);

          // Initialize to middle slice
          const middleSlice = Math.floor(count / 2);
          setCurrentSlice(middleSlice);
        }
      } catch (err) {
        console.error('Error loading volume:', err);
        setError(`Failed to load volume: ${err.message}`);
        setVolumeLoading(false);
      }
    };

    loadVolumeAndInfo();
  }, [viewType, API_URL]);

  // Handle mouse wheel for smooth scrolling
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Use larger steps for faster scrolling with shift key
    const step = e.shiftKey ? 5 : 1;
    const delta = e.deltaY > 0 ? step : -step;

    setCurrentSlice(prev => {
      const newSlice = prev + delta;
      return Math.max(0, Math.min(totalSlices - 1, newSlice));
    });
  }, [totalSlices]);

  // Handle drag scrolling
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartSlice.current = currentSlice;
  }, [currentSlice]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - dragStartY.current;
    const sliceDelta = Math.round(deltaY / 3);
    const newSlice = dragStartSlice.current - sliceDelta;

    setCurrentSlice(Math.max(0, Math.min(totalSlices - 1, newSlice)));
  }, [isDragging, totalSlices]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    const step = e.shiftKey ? 10 : 1;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        setCurrentSlice(prev => Math.max(0, prev - step));
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        setCurrentSlice(prev => Math.min(totalSlices - 1, prev + step));
        break;
      default:
        break;
    }
  }, [totalSlices]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Native wheel listener: React's onWheel is passive, so preventDefault
  // would be a no-op and the page would scroll while scrubbing slices
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Fetch slice data
  useEffect(() => {
    if (volumeLoading || totalSlices === 0) return;

    const fetchSliceData = async () => {
      const viewLower = viewType.toLowerCase();

      try {
        const data = await ctSliceCache.fetchSlice(viewLower, currentSlice);

        if (!data) {
          throw new Error('Failed to load slice data');
        }

        setSliceData(data);
        setLoading(false);

        // Update vertebra info
        if (data.prominent_vertebra) {
          setProminentVertebra(data.prominent_vertebra);
          if (onSliceChange) {
            onSliceChange({
              sliceIndex: currentSlice,
              viewType: viewLower,
              detectedVertebrae: data.vertebrae,
              prominentVertebra: data.prominent_vertebra
            });
          }
        } else {
          setProminentVertebra(null);
        }

        // No incremental preloading needed - all slices are already loaded
      } catch (err) {
        console.error(`Error loading slice data:`, err);
        setError(`Failed to load slice data: ${err.message}`);
        setLoading(false);
      }
    };

    fetchSliceData();
  }, [viewType, currentSlice, totalSlices, volumeLoading, onSliceChange]);

  // Windowed preloading - load nearby slices for smooth scrolling
  useEffect(() => {
    if (totalSlices === 0) return;

    const viewLower = viewType.toLowerCase();
    // Preload +/- 10 slices around current position
    // This runs in background via ctSliceCache queue
    ctSliceCache.preloadSlices(viewLower, currentSlice, totalSlices, 10);
  }, [viewType, currentSlice, totalSlices]);

  // Render slice to canvas
  useEffect(() => {
    if (!sliceData || !canvasRef.current) return;

    // Cancel any pending render
    if (renderRequestRef.current) {
      cancelAnimationFrame(renderRequestRef.current);
    }

    // Schedule new render
    renderRequestRef.current = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      // Set canvas size
      canvas.width = sliceData.width;
      canvas.height = sliceData.height;


      // Create image data for CT
      const imageData = ctx.createImageData(sliceData.width, sliceData.height);
      const data = imageData.data;

      // Convert flat array back to typed array
      const ctData = new Uint8Array(sliceData.ct_data);

      // Fill image data with CT values
      for (let i = 0; i < ctData.length; i++) {
        const value = ctData[i];
        const idx = i * 4;
        data[idx] = value;
        data[idx + 1] = value;
        data[idx + 2] = value;
        data[idx + 3] = 255;
      }

      // Draw CT image
      ctx.putImageData(imageData, 0, 0);

      // Draw segmentation overlay if enabled
      if (showOverlay && sliceData.seg_data) {
        const segData = new Uint8Array(sliceData.seg_data);

        // Get actual segmentation dimensions from backend
        const segActualWidth = sliceData.seg_width || sliceData.width;
        const segActualHeight = sliceData.seg_height || sliceData.height;

        // Initialize or resize offscreen canvas if needed
        if (!offscreenCanvasRef.current) {
          offscreenCanvasRef.current = document.createElement('canvas');
        }

        const tempCanvas = offscreenCanvasRef.current;
        if (tempCanvas.width !== segActualWidth || tempCanvas.height !== segActualHeight) {
          tempCanvas.width = segActualWidth;
          tempCanvas.height = segActualHeight;
        }

        const tempCtx = tempCanvas.getContext('2d');

        // Create image data for segmentation with its actual dimensions
        const segImageData = tempCtx.createImageData(segActualWidth, segActualHeight);
        const segPixelData = segImageData.data;

        // Fill segmentation data with colors

        // Fill the segmentation data
        for (let i = 0; i < segData.length; i++) {
          const label = segData[i];
          const pixelIdx = i * 4;

          if (label > 0 && label <= 25) {
            const color = getVertebraColor(label, 'rgb');
            segPixelData[pixelIdx] = color[0];
            segPixelData[pixelIdx + 1] = color[1];
            segPixelData[pixelIdx + 2] = color[2];
            segPixelData[pixelIdx + 3] = 128; // Semi-transparent
          } else {
            // Make background fully transparent
            segPixelData[pixelIdx] = 0;
            segPixelData[pixelIdx + 1] = 0;
            segPixelData[pixelIdx + 2] = 0;
            segPixelData[pixelIdx + 3] = 0;
          }
        }

        // Put the segmentation data on the temp canvas
        tempCtx.putImageData(segImageData, 0, 0);

        // Draw the segmentation overlay on top of the CT, scaling to match CT dimensions
        ctx.save();
        ctx.globalAlpha = 0.6;
        // Scale the segmentation to match the CT dimensions
        ctx.drawImage(tempCanvas, 0, 0, segActualWidth, segActualHeight, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      }
    });
  }, [sliceData, showOverlay, viewType]);

  // Handle slice slider change
  const handleSliderChange = (e) => {
    setCurrentSlice(parseInt(e.target.value));
  };

  if (error) {
    return (
      <div className="viewer" style={styles.container}>
        <div className="alert alert--danger" style={styles.errorContainer}>
          <div style={styles.errorBody}>
            <h3 style={styles.errorTitle}>Error Loading Image</h3>
            <p style={styles.errorText}>{error}</p>
            <p style={styles.errorHint}>Make sure the backend server is running at {API_URL}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="viewer"
      tabIndex={0}
      style={{
        ...styles.container,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      {(loading || volumeLoading) && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div className="spinner" />
            <span style={styles.loadingText}>Loading CT volume…</span>
          </div>
        </div>
      )}

      <div style={styles.vtkContainer}>
        <canvas
          ref={canvasRef}
          style={styles.canvas}
          onContextMenu={(e) => e.preventDefault()}
        />

        {totalSlices > 0 && (
          <div className="viewer__hud" style={styles.textOverlay}>
            {viewType} View - Slice {currentSlice + 1}/{totalSlices}
            {prominentVertebra && ` - ${prominentVertebra}`}
          </div>
        )}
      </div>

      {/* Slice navigation bar */}
      <div style={styles.sliceControls}>
        <button
          type="button"
          className="btn btn--ghost"
          aria-label="Previous slice"
          style={styles.navButton}
          disabled={currentSlice <= 0 || totalSlices === 0}
          onClick={() => setCurrentSlice(prev => Math.max(0, prev - 1))}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2.5 4.5 7 9 11.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div style={styles.sliderContainer}>
          <input
            type="range"
            min="0"
            max={Math.max(0, totalSlices - 1)}
            value={currentSlice}
            onChange={handleSliderChange}
            className="ct-viewer-slider"
            disabled={totalSlices === 0}
          />
        </div>

        <button
          type="button"
          className="btn btn--ghost"
          aria-label="Next slice"
          style={styles.navButton}
          disabled={currentSlice >= totalSlices - 1 || totalSlices === 0}
          onClick={() => setCurrentSlice(prev => Math.min(totalSlices - 1, prev + 1))}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M5 2.5 9.5 7 5 11.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Runtime styles: spinner keyframes (legacy) + scoped range-slider styling,
// guarded so the tag is only injected once.
if (typeof document !== 'undefined' && !document.getElementById('ct-viewer-spinner-styles')) {
  const style = document.createElement('style');
  style.id = 'ct-viewer-spinner-styles';
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .ct-viewer-slider {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 4px;
      border-radius: var(--radius-full, 999px);
      background: rgba(255, 255, 255, 0.14);
      outline: none;
      cursor: pointer;
      transition: opacity var(--dur-fast, 160ms) ease;
    }
    .ct-viewer-slider:disabled {
      opacity: 0.35;
      cursor: default;
    }
    .ct-viewer-slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: none;
      background: var(--accent, #4f9cf0);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
      transition: transform 160ms var(--ease-spring, ease);
    }
    .ct-viewer-slider:not(:disabled)::-webkit-slider-thumb:hover {
      transform: scale(1.2);
    }
    .ct-viewer-slider:not(:disabled):active::-webkit-slider-thumb {
      transform: scale(1.05);
    }
    .ct-viewer-slider::-moz-range-track {
      height: 4px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.14);
    }
    .ct-viewer-slider::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: none;
      background: var(--accent, #4f9cf0);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
      transition: transform 160ms var(--ease-spring, ease);
    }
    .ct-viewer-slider:not(:disabled)::-moz-range-thumb:hover {
      transform: scale(1.2);
    }
  `;
  document.head.appendChild(style);
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  },
  vtkContainer: {
    position: 'relative',
    width: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--bg-viewer)'
  },
  canvas: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain'
  },
  textOverlay: {
    top: '10px',
    left: '10px',
    maxWidth: 'calc(100% - 20px)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    pointerEvents: 'none'
  },
  sliceControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-3)',
    padding: '10px 14px',
    background: 'rgba(10, 12, 15, 0.72)',
    borderTop: '1px solid var(--line-subtle)',
    zIndex: 10
  },
  sliderContainer: {
    flex: 1,
    minWidth: 0,
    maxWidth: '520px',
    display: 'flex',
    alignItems: 'center'
  },
  navButton: {
    width: '30px',
    height: '30px',
    padding: 0,
    borderRadius: 'var(--radius-full)',
    flexShrink: 0
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(10, 12, 15, 0.7)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    zIndex: 20
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-3)'
  },
  loadingText: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: 'var(--text-secondary)'
  },
  errorContainer: {
    margin: 'auto',
    width: 'min(420px, calc(100% - 32px))'
  },
  errorBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-2)',
    minWidth: 0
  },
  errorTitle: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: 600,
    letterSpacing: '-0.02em'
  },
  errorText: {
    margin: 0,
    color: 'var(--text-primary)',
    overflowWrap: 'anywhere'
  },
  errorHint: {
    margin: 0,
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
    overflowWrap: 'anywhere'
  }
};

export default CTSliceViewer;
